import { CheckIcon, CloseIcon } from "@chakra-ui/icons";
import { Box, Button, Text } from "@chakra-ui/react";
import { CustomModal } from "components/custom-modal";
import { DropdownOptionProps } from "components/dropdown";
import { useState } from "react";
import { saveOrUpdateUsuarioBanco } from "./utils";
import { makeEvent, useEventListener } from "services/events";
import { UsuarioBanco } from "../page";
import { DropdownField } from "components/dropdown-field";
import { RefinSaqueAndDigitadorFields } from "./refin-saque-digitador-fields";
import { MargemConveniosFields } from "./margem-convenios-fields";
import { InputBoxSearch } from "components/input-box-search";
import { useApplicationContext } from "contexts/ApplicationContext";
import { useFilterEmpresa } from "../utils";
import { isUserGerente } from "utils/is-gerente";

interface OpenParams {
  row?: UsuarioBanco;
  customerId: number | undefined;
}
export interface UsuarioBancoErrors {
  banco: boolean;
  username: boolean;
  password: boolean;
  cpfUsuarioCertificado: boolean;
  nomeUsuarioCertificado: boolean;
  codigoDigitador: boolean;
  codigoPromotora: boolean;
  tokenModulo: boolean;
  botIdCronowise: boolean;
  nomePromotora: boolean;
  apiKey: boolean;
}

const initialErrorsValue = {
  banco: false,
  username: false,
  password: false,
  cpfUsuarioCertificado: false,
  nomeUsuarioCertificado: false,
  codigoDigitador: false,
  codigoPromotora: false,
  tokenModulo: false,
  botIdCronowise: false,
  nomePromotora: false,
  apiKey: false,
};

const initialModalDataValue: UsuarioBanco & { customerId: number | undefined } =
  {
    id: "",
    banco: "",
    username: "",
    password: "",
    cpfUsuarioCertificado: "",
    nomeUsuarioCertificado: "",
    codigoDigitador: "",
    tokenModulo: "",
    botIdCronowise: "",
    ativoDigitador: false,
    ativoHigienizador: false,
    ativoHigienizadorConvenios: false,
    customerId: undefined,
  };

export function UsuariosBancosModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [modalData, setModalData] = useState<UsuarioBanco>(
    initialModalDataValue
  );
  const [errors, setErrors] = useState<UsuarioBancoErrors>(initialErrorsValue);
  const [isUpdate, setIsUpdate] = useState(false);
  const { user } = useApplicationContext();
  let { empresas, loadingEmpresas, onChangeEmpresa, selectedEmpresa } =
    useFilterEmpresa({
      onChange: (customerId) => {
        selectedEmpresa = customerId;
      },
    });

  const allFalse =
    !modalData.ativoDigitador &&
    !modalData.ativoHigienizador &&
    !modalData.ativoHigienizadorConvenios;

  let dropdownSelectUserType: string[] = [];
  if (modalData.ativoDigitador) dropdownSelectUserType.push("DIGITADOR");
  if (modalData.ativoHigienizador) dropdownSelectUserType.push("REFIN_SAQUE");
  if (modalData.ativoHigienizadorConvenios)
    dropdownSelectUserType.push("MARGEM_CONVENIOS");

  const onOpen = (props: OpenParams) => {
    setIsOpen(true);
    setIsUpdate(!!props?.row);
    setModalData(props?.row ?? initialModalDataValue);
    if (isGerente) onChangeEmpresa(props.customerId);
  };

  const onClose = () => {
    setIsOpen(false);
    setErrors(initialErrorsValue);
  };

  const onChangeUsuarioBancoDialog = (
    field: keyof UsuarioBanco,
    value: any
  ) => {
    setErrors({ ...errors, [field]: false });
    const newValue = { ...modalData, [field]: value };
    setModalData(newValue);
  };

  useEventListener("openModalUsuariosBancos", onOpen);

  const usuarioBancoDialogFooter = (
    <>
      <Button
        leftIcon={<CheckIcon />}
        onClick={() =>
          saveOrUpdateUsuarioBanco({
            errors,
            modalData,
            setIsLoading,
            onClose,
            setErrors,
            customerId: selectedEmpresa || user.userData.customerId,
            user
          })
        }
        isLoading={isLoading}
        loadingText="Salvando"
      >
        Salvar
      </Button>
      <Button
        leftIcon={<CloseIcon w="12px" h="12px" />}
        variant="outline"
        onClick={onClose}
      >
        Cancelar
      </Button>
    </>
  );

  const dropdownUserType: DropdownOptionProps[] = [
    {
      name: "Refin e Saque",
      value: "REFIN_SAQUE",
      isDisabled: () => !!modalData.ativoHigienizadorConvenios,
    },
    {
      name: "Digitador",
      value: "DIGITADOR",
      isDisabled: () => !!modalData.ativoHigienizadorConvenios,
    },
    {
      name: "Margem Convênios",
      value: "MARGEM_CONVENIOS",
      isDisabled: () => modalData.ativoDigitador || modalData.ativoHigienizador,
    },
  ];

  const getNameUserType = (value: string) =>
    dropdownUserType.find((c) => c.value === value)?.name;

  const isGerente = isUserGerente(user);

  return (
    <CustomModal
      isOpen={isOpen}
      minW="450px"
      modalTitle="Usuário Banco"
      modalFooter={usuarioBancoDialogFooter}
      onClose={onClose}
      finalFocusRef={{ current: null }}
      containerProps={{
        justifyContent: "flex-start",
        alignItems: "center",
        flexDirection: "column",
      }}
    >
      {isGerente &&
      user.userData.customerChildrenInheritUsersBancos !== true ? (
        <Box w="100%" mb="16px" mr="8px">
          <Text mb="8px" fontWeight="medium">
            Empresa
          </Text>
          <InputBoxSearch
            options={empresas}
            isDisabled
            onChange={onChangeEmpresa}
            checkIsInclude={(option) => {
              return selectedEmpresa === option.value;
            }}
            isLoading={loadingEmpresas}
            value={
              empresas.find((customer) => selectedEmpresa === customer.value)
                ?.name
            }
            inputBoxProps={{
              bg: "#fff",
              onFocus: (e) => {
                e.currentTarget.textContent = "";
              },
              onBlur: (e) => {
                e.currentTarget.textContent =
                  empresas.find(
                    (customer) => selectedEmpresa === customer.value
                  )?.name || "";
              },
            }}
            openOnFocus
            closeOnSelect
          />
        </Box>
      ) : null}
      <Box mb="15px">
        <DropdownField
          options={dropdownUserType}
          value={dropdownSelectUserType}
          onChange={(value) => {
            if (value === "REFIN_SAQUE") {
              onChangeUsuarioBancoDialog(
                "ativoHigienizador",
                !modalData.ativoHigienizador
              );
            }
            if (value === "DIGITADOR") {
              onChangeUsuarioBancoDialog(
                "ativoDigitador",
                !modalData.ativoDigitador
              );
            }
            if (value === "MARGEM_CONVENIOS") {
              onChangeUsuarioBancoDialog(
                "ativoHigienizadorConvenios",
                !modalData.ativoHigienizadorConvenios
              );
            }
          }}
          title="Tipo Usuário"
          dropdownProps={{
            w: "100%",
            multiSelect: true,
            children: dropdownSelectUserType
              .map((v) => getNameUserType(v))
              .join(", "),
          }}
        />
      </Box>

      {allFalse ? null : modalData.ativoHigienizador ||
        modalData.ativoDigitador ? (
        <RefinSaqueAndDigitadorFields
          modalData={modalData}
          onChangeUsuarioBancoDialog={onChangeUsuarioBancoDialog}
          errors={errors}
          isUpdate={isUpdate}
        />
      ) : (
        <MargemConveniosFields
          modalData={modalData}
          onChangeUsuarioBancoDialog={onChangeUsuarioBancoDialog}
          errors={errors}
        />
      )}
    </CustomModal>
  );
}

export const openModalUsuariosBancos = (props: OpenParams) =>
  makeEvent("openModalUsuariosBancos", props);
